import SftGlobalData from './SftGlobalData';

/**
 * Display-only rewrite of images and videos that live on a hoster preview host
 * (….webhosting.be).
 *
 * Such a host serves a self-signed certificate until the real domain is live.
 * The admin and the Unlayer editor iframe are https pages, and browsers
 * auto-upgrade an http:// image there to https:// -- which then fails on the
 * certificate, so the image shows broken although the shop has it. Auth's
 * webshop-asset.php fetches it server-side and serves it from a valid cert
 * (videos too, with Range support so they can seek).
 *
 * toPreviewSafe() swaps such media URLs for the passthrough; fromPreviewSafe()
 * puts the ORIGINAL string back verbatim (it travels url-encoded in `u`), so a
 * saved design never contains the passthrough URL. Round trip is lossless:
 * anything after the media URL (e.g. `&b=2` of a query) is left in place and
 * comes back unchanged.
 */

// scheme optional (protocol-relative //host/...), path must end in a media
// extension; stops before quotes, whitespace, <>, ), backslash (JSON-escaped
// quote) and & (query separator / HTML entity)
const PREVIEW_MEDIA_RE = /(?:https?:)?\/\/(?:[a-z0-9-]+\.)*webhosting\.be(?::\d+)?\/[^\s"'<>)\\&?#]*\.(?:jpe?g|png|gif|webp|avif|svg|bmp|ico|mp4|webm|m4v|mov|ogv)(?:\?[^\s"'<>)\\&#]*)?/gi;

// One source for the passthrough address: the auth base the admin talks to.
// A saved design never contains it (fromPreviewSafe runs on every way out), so
// only the base of THIS session has to be recognised on the way back.
const passthroughBase = () => `${SftGlobalData.baseURL_auth}webshop-asset.php?u=`;

const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const passthroughRe = () => new RegExp(`${escapeRegExp(passthroughBase())}([^\\s"'<>)\\\\&]+)`, 'gi');

export const hasPreviewHostImage = (str) => typeof str === 'string' && /webhosting\.be/i.test(str);

export const toPreviewSafe = (str) => {
    if (!hasPreviewHostImage(str)) return str;
    const base = passthroughBase();
    // the exact original goes into `u` (a protocol-relative //host/... too:
    // webshop-asset.php adds a scheme itself), so the way back is verbatim
    return str.replace(PREVIEW_MEDIA_RE, (url) => base + encodeURIComponent(url));
};

// Unlayer's export appends its own size hint to an image URL that already has
// a query (`…webshop-asset.php?u=…&w=715.4px`). Put back on the original
// `…/logo.png` that became `…/logo.png&w=715.4px`: a path, not a file, so the
// shop answered 302 and the voucher PDF lost its logo (delaere, 19/10).
const UNLAYER_SIZE_HINT_RE = /^(?:&(?:amp;)?[wh]=[\d.]+(?:px)?)+/i;

export const fromPreviewSafe = (str) => {
    if (typeof str !== 'string' || str.indexOf('webshop-asset.php') === -1) return str;
    const re = passthroughRe();
    let out = '';
    let last = 0;
    let m;
    while ((m = re.exec(str)) !== null) {
        let original;
        try {
            original = decodeURIComponent(m[1]);
        } catch {
            continue;
        }
        out += str.slice(last, m.index) + original;
        last = m.index + m[0].length;
        // only when the original had no query of its own: then nothing after
        // the passthrough can belong to it
        if (original.indexOf('?') === -1) {
            const hint = UNLAYER_SIZE_HINT_RE.exec(str.slice(last));
            if (hint) last += hint[0].length;
        }
    }
    return last === 0 ? str : out + str.slice(last);
};

/**
 * For a single src / background URL in an admin preview (<img>, <video>,
 * css url()). blob:/data: previews of a fresh upload and URLs on any other
 * host come back untouched. Display only -- never store the result.
 */
export const previewSafeUrl = (url) => (typeof url === 'string' ? toPreviewSafe(url) : url);

/** Same, for a design object (Unlayer JSON). Returns the input when untouched. */
const mapDesign = (design, fn) => {
    if (!design || typeof design !== 'object') return design;
    try {
        const json = JSON.stringify(design);
        const out = fn(json);
        return out === json ? design : JSON.parse(out);
    } catch {
        return design;
    }
};

export const designToPreviewSafe = (design) => mapDesign(design, toPreviewSafe);
export const designFromPreviewSafe = (design) => mapDesign(design, fromPreviewSafe);
